import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
import { MaskedLines, Reveal, EASE } from './reveal';

// Exported so HammerLanding can build FAQPage JSON-LD from the same source
// of truth — the visible copy and the structured data never drift apart.
export const HAMMER_FAQ_ITEMS = [
  {
    q: 'What size pilings can the Absolute Hammer drive?',
    a: 'Up to 12" standard piles.',
  },
  {
    q: 'What air compressor do I need to run it?',
    a: 'Any standard shop compressor rated 100+ PSI. No hydraulic power pack, no proprietary fuel — just plug in air and go.',
  },
  {
    q: 'How is the pneumatic lift-and-drop different from other pile hammers?',
    a: "Most hammers use air pressure only to lift the ram, then let gravity do the hammering. The Absolute Hammer uses air to both lift AND drop the ram — every stroke is a full-power stroke instead of a passive fall.",
  },
  {
    q: 'What is the "black box" controller and why is it off the hammer?',
    a: "It is our proprietary controller. We keep it off the hammer so its electronics aren't beaten to death by the strikes it commands — the same reason other rigs need constant control-box repairs.",
  },
  {
    q: "What's included in the $10,500 price?",
    a: 'The fully galvanized hammer, the off-hammer proprietary controller, and a 1-year warranty on all electronic components. Shipping is quoted separately based on your destination.',
  },
  {
    q: 'Where do you ship?',
    a: "Anywhere in the Lower 48. We're marine contractors ourselves and used to hauling heavy equipment — we'll bring it to you without charging an arm and a leg.",
  },
  {
    q: 'How long will it take to get my hammer?',
    a: 'Hammers are in stock (2 units this run) and expedited shipping is available, so no job is left hanging. Transit time depends on your location — we\'ll give you a firm delivery estimate with your freight quote.',
  },
  {
    q: "What's covered under warranty?",
    a: '1-year full warranty on the controller and every electronic component. The rest of the hammer is fully galvanized steel — built to outlast its warranty by a wide margin.',
  },
  {
    q: 'Where is it built?',
    a: 'Designed and built in-house at our shop in Fort Myers, Florida. Not resold, not rebadged — this is our hammer.',
  },
  {
    q: 'Do you offer financing?',
    a: 'No — the Absolute Hammer is cash only.',
  },
];

function FaqItem({ item, isOpen, onToggle, index }) {
  const Icon = isOpen ? Minus : Plus;
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6, ease: EASE, delay: index * 0.06 }}
      className={`rounded-xl overflow-hidden bg-black/40 transition-colors ${
        isOpen ? 'border border-cyan-400/60' : 'border border-cyan-400/25 hover:border-cyan-400/50'
      }`}
      data-testid={`hammer-faq-item-${index}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-6 text-left px-5 sm:px-7 py-5 sm:py-6"
        data-testid={`hammer-faq-question-${index}`}
      >
        <span className="font-display text-white text-lg sm:text-xl font-semibold leading-snug tracking-tight">
          {item.q}
        </span>
        <span
          className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-cyan-400/50 flex items-center justify-center text-cyan-300 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <Icon className="w-4 h-4" />
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p
            className="px-5 sm:px-7 pb-6 sm:pb-7 text-zinc-400 text-sm leading-relaxed max-w-3xl"
            data-testid={`hammer-faq-answer-${index}`}
          >
            {item.a}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function HammerFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="hammer-faq"
      data-testid="hammer-faq-section"
      className="relative bg-black py-16 sm:py-20 lg:py-32 overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <span className="h-px w-8 sm:w-10 bg-cyan-400" />
              <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                A Deeper Dive
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.05] sm:leading-[1.02] tracking-tight">
            <MaskedLines
              delay={0.15}
              lines={[
                'Frequently asked',
                <span className="text-cyan-300">questions.</span>,
              ]}
            />
          </h2>
        </div>

        <div className="mt-10 sm:mt-14 space-y-3 sm:space-y-4">
          {HAMMER_FAQ_ITEMS.map((item, i) => (
            <FaqItem
              key={item.q}
              index={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
